/**
 * Header — porta fiel de `mountLayout` [application.js:2015].
 * DOM idêntico ao legado para reaproveitar 100% do global.css:
 *   header > div.wrapper.flex.flex_center >
 *     div.logo > img#logo
 *     nav#sidebar > ul > li (Início) | li.sub (Listas/Ferramentas/Conteúdos/Conta) > button + ol
 *     div.support > ul > li.capture > button#capture
 * Visibilidade por módulos (dashboard/lists/tools/contents) igual ao legado. A Conta aparece
 * sempre: desde 24/10/2026 (decisão 195) ela ocupa o lugar do Suporte. Proposta B do Claude
 * Design (25/10/2026): no topo, o email e a situação da licença (ícone, frase e, quando há o que
 * fazer, Renovar ou Ver os planos); depois Licença e Perfil e preferências, com descrição, o
 * Programa de recomendação e, depois do separador, a ajuda. Pedido de acesso esperando: número
 * em Conta na barra e "1 pedido" no item Licença, que leva ao pedido.
 * "Capturar Leads" abre o dropdown de categorias (showCategories), não navega.
 *
 * Movimento (23/10/2026, guia/10 "Barra do painel e troca de página" e guia/08 "Menus"):
 *  - o traço amarelo de 4 px da página atual é um só (.ds-traco) e desliza de um item para o
 *    outro em 280 ms;
 *  - os botões da barra respondem só na cor (.ds-resposta, M1 a);
 *  - os menus abrem com .ds-menu + .is-aberto (140 ms, sai em 80 ms, volta do meio), pelo
 *    clique, pelo teclado e com o mouse em cima; a seta gira (.ds-seta).
 */
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { useSessionStore } from '../../stores/sessionStore';
import { useCaptureStore } from '../../stores/captureStore';
import { CaptureCategories } from '../capture/CaptureCategories';
import { useLiveRun } from '../capture/full/useLiveRun';
import { plainText } from '../capture/captureText';
import { useCaptureRunStore } from '../../stores/captureRunStore';
import { useWatchdogStore } from '../../stores/watchdogStore';
import { ContactDialog } from '../../features/contact/ContactDialog';
import { useGuiaStore } from '../../features/guia/guiaStore';
import { comecarGuia } from '../../features/guia/guiaAcoes';
import { useGuiaText, type GuiaKey } from '../../features/guia/guiaText';
import { marcoDe } from '../../features/guia/passos';
import { useBoasVindasStore } from '../../features/boasVindas/boasVindasStore';
import { useBoasVindasText } from '../../features/boasVindas/boasVindasText';
import { assetUrls, externalUrls } from '../../config/env';
import { emitActivity } from '../../lib/activity';
import { prefetchPage } from '../../routes/prefetch';
import { useWhatsappGatewayAvailable } from '../../features/whatsappGroups/useWhatsappGatewayAvailable';
import { VISIBLE_LIST_MODULES } from '../../config/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accountOverviewKey, fetchAccountOverview } from '../../api/account';
import { unwrap } from '../../lib/apiClient';
import { has, isTrial } from '../../lib/entitlements';
import { useAccountText } from '../../features/account/accountText';
import { licenseView, renewIsUrgent, type LicenseKind } from '../../features/account/licenseView';
import { openRenewal } from '../../features/account/renewal';
import { useCtaStore } from '../../features/account/ctaStore';

// Progresso da captura no "Capturar Leads": só carrega quando há uma captura andando.
const CaptureRunButton = lazy(() =>
  import('../capture/full/CaptureRunButton').then((m) => ({ default: m.CaptureRunButton })),
);
const CaptureRunToast = lazy(() =>
  import('../capture/full/CaptureRunButton').then((m) => ({ default: m.CaptureRunToast })),
);

/** Ordem dos módulos no dropdown Listas — a do legado, sem o Hunter (places, phones, emails). */
const LIST_ORDER = VISIBLE_LIST_MODULES;

/** Os quatro marcos do guia passo a passo ("Você parou em: Monte a busca"). */
const GUIA_MARCOS: GuiaKey[] = ['m1', 'm2', 'm3', 'm4'];

/** Rotas do menu Conteúdos: o item da barra fica marcado em qualquer uma delas. */
const CONTENT_PATHS = ['/leadtalk', '/leadmusic', '/ebook'];

/** Itens do submenu de um item da barra. */
function menuItems(li: Element | null | undefined): HTMLButtonElement[] {
  return li ? [...li.querySelectorAll<HTMLButtonElement>('ol.hx-menu button.hx-item')] : [];
}

/** Altura do traço da página atual (a borda de baixo dos botões da barra). */
const TRACO_ALTURA = 4;

/**
 * Põe o traço amarelo sob o item da página atual: translate até o botão e scaleX na largura dele
 * (o traço tem 1 px; .ds-traco anima o transform). Sem `animar` (abertura, mudança de tamanho
 * da barra) ou vindo de nenhum item, vai direto, sem transição. Sem página atual na barra, some.
 */
function posicionarTraco(nav: HTMLElement, traco: HTMLElement, animar: boolean): void {
  const botao = nav.querySelector<HTMLElement>('ul > li.active > button');
  if (!botao) {
    traco.style.opacity = '0';
    delete traco.dataset.alvo;
    return;
  }
  const n = nav.getBoundingClientRect();
  const b = botao.getBoundingClientRect();
  const alvo = `translate(${b.left - n.left}px, ${b.bottom - n.top - TRACO_ALTURA}px) scaleX(${b.width})`;
  // Já está lá (ou indo para lá): não interrompe o deslize.
  if (traco.dataset.alvo === alvo) return;
  const direto = !animar || !traco.dataset.alvo;
  traco.dataset.alvo = alvo;
  if (direto) traco.style.transition = 'none';
  traco.style.transform = alvo;
  traco.style.opacity = '1';
  if (direto) {
    // Aplica a posição antes de devolver a transição do CSS.
    traco.getBoundingClientRect();
    traco.style.transition = '';
  }
}

interface MenuItemProps {
  id: string;
  title: string;
  sub?: string;
  /** Página atual: fundo claro com a barra amarela à esquerda. */
  current?: boolean;
  /** Texto para leitor de tela quando o item abre outra aba. */
  external?: string;
  /** Selo "Novo". */
  badge?: string;
  /** Ícone à esquerda (menu Conta, como no protótipo). */
  icon?: string;
  /** Selo de contagem à direita ("1 pedido"). */
  pill?: string;
  /** Item em destaque, com fundo claro (o guia passo a passo no menu Conta). */
  destaque?: boolean;
  onClick: () => void;
}

/** Ícone da situação da licença no topo do menu Conta: a cor nunca vai sozinha. */
function statusIcon(kind: LicenseKind): string {
  if (kind === 'ending' || kind === 'today') return 'schedule';
  if (kind === 'expired') return 'error';
  if (kind === 'trial') return 'lock';
  return 'check_circle';
}

/** Tom do ícone: verde, âmbar, vermelho ou neutro (o teste é informação, não atenção). */
function statusTone(kind: LicenseKind): 'ok' | 'warn' | 'off' | 'neutral' {
  if (kind === 'ending' || kind === 'today') return 'warn';
  if (kind === 'expired') return 'off';
  if (kind === 'trial') return 'neutral';
  return 'ok';
}

/** Seta do item com menu: gira 180° ao abrir (.ds-seta, 140 ms, curva de movimento). */
function Seta(): JSX.Element {
  return (
    <span className="hx-seta ds-seta material-symbols-rounded" aria-hidden="true">
      expand_more
    </span>
  );
}

/** Item dos submenus do cabeçalho (proposta "Menu", 18/10/2026). */
function MenuItem({
  id,
  title,
  sub,
  current,
  external,
  badge,
  icon,
  pill,
  destaque,
  onClick,
}: MenuItemProps): JSX.Element {
  return (
    <li className={current ? 'active' : undefined} role="none">
      <button
        type="button"
        id={id}
        role="menuitem"
        // Fora da ordem do Tab: dentro do menu andam as setas; o Tab sai do menu.
        tabIndex={-1}
        className={destaque ? 'hx-item hx-item--destaque' : 'hx-item'}
        aria-current={current ? 'page' : undefined}
        onClick={onClick}
      >
        {icon ? (
          <span className="hx-item__icon material-symbols-rounded" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="hx-item__copy">
          <span className="hx-item__title">
            {title}
            {/* Selo "Novo": entra com o fade do menu (.ds-menu), sem animação própria nem
                pulso. O menu fica montado e escondido, então um fade só do selo rodaria na
                carga da página, sem ninguém ver. */}
            {badge ? <span className="hx-new">{badge}</span> : null}
            {/* Na linha do nome, à direita: a descrição fica com a largura toda. */}
            {pill ? <span className="hx-pill">{pill}</span> : null}
          </span>
          {sub ? <span className="hx-item__sub">{sub}</span> : null}
        </span>
        {external ? (
          <>
            <span className="hx-item__ext material-symbols-rounded" aria-hidden="true">
              open_in_new
            </span>
            <span className="hx-sr"> {external}</span>
          </>
        ) : null}
      </button>
    </li>
  );
}

export function Header(): JSX.Element {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSessionStore((s) => s.session);
  const [logoReady, setLogoReady] = useState(false);
  // Popup de categorias no store: abre pelo botão do header E pelo card da home.
  const categoriesOpen = useCaptureStore((s) => s.categoriesOpen);
  const openCategories = useCaptureStore((s) => s.openCategories);
  const closeCategories = useCaptureStore((s) => s.closeCategories);
  const openCaptureWindow = useCaptureStore((s) => s.openCaptureWindow);
  const captureWindow = useCaptureStore((s) => s.captureWindow);
  // Captura de Emails/Telefones andando fora da janela de busca: o botão vira o progresso.
  const liveRun = useLiveRun();
  const runToast = useCaptureRunStore((s) => s.toastAt);
  const [contactOpen, setContactOpen] = useState(false);
  const { tx: gtx } = useGuiaText();
  const btx = useBoasVindasText();
  const guiaLigado = useGuiaStore((g) => g.status === 'ativo');
  const guiaPasso = useGuiaStore((g) => g.passo);
  // Submenu aberto pelo clique ou pelo teclado.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Submenu mostrado pelo mouse em cima (antes era só o :hover do CSS; agora o .is-aberto da
  // .ds-menu precisa saber, para entrar e sair com transição).
  const [hoverMenu, setHoverMenu] = useState<string | null>(null);
  // O "Capturar Leads" já entra parado: sem o tremor e sem o fade do legado (21/10/2026). É o
  // único amarelo cheio da barra.
  const navRef = useRef<HTMLElement | null>(null);
  const tracoRef = useRef<HTMLSpanElement | null>(null);
  const tracoPosto = useRef(false);

  const path = location.pathname;
  // Itens que a barra mostra (sessão e marca). Mudam sem troca de página (uma compra ou um
  // resgate trazem outros módulos) e podem empurrar o item atual para o lado.
  const modulosBarra = session?.modules ?? {};
  const itensBarra = [
    modulosBarra['dashboard'],
    modulosBarra['emails'] || modulosBarra['phones'] || modulosBarra['places'],
    modulosBarra['tools'],
    brand.source === 'lead-machine' && modulosBarra['contents'],
  ]
    .map((v) => (v ? '1' : '0'))
    .join('');

  // Fecha o submenu ao trocar de página e ao clicar fora da barra.
  useEffect(() => setOpenMenu(null), [path]);

  // Movimento (23/10/2026): o traço da página atual desliza do item antigo para o novo
  // (.ds-traco, 280 ms); na abertura ele já nasce no lugar. Antes da pintura, para não piscar.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const traco = tracoRef.current;
    if (!nav || !traco) return;
    posicionarTraco(nav, traco, tracoPosto.current);
    tracoPosto.current = true;
  }, [path]);

  // A barra muda sem troca de página: fonte que chega, texto de um item, itens que entram ou
  // saem (módulos da sessão). O traço acompanha sem animar. O tamanho da lista não muda com
  // os itens (ela ocupa a largura da barra), então o observador olha também cada item: um
  // item que cresce empurra os seguintes. Itens novos refazem a observação e o traço vai ao
  // lugar antes da pintura.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const traco = tracoRef.current;
    const lista = nav?.querySelector('ul');
    if (!nav || !traco || !lista) return undefined;
    const medir = (): void => posicionarTraco(nav, traco, false);
    // Na abertura o traço já está no lugar (efeito acima) e isto não faz nada; com itens
    // novos, vai direto à posição nova.
    medir();
    let observador: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'function') {
      observador = new ResizeObserver(medir);
      observador.observe(lista);
      for (const item of lista.children) observador.observe(item);
    }
    let vivo = true;
    void document.fonts?.ready.then(() => {
      if (vivo) medir();
    });
    return () => {
      vivo = false;
      observador?.disconnect();
    };
  }, [itensBarra]);
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: globalThis.MouseEvent): void => {
      if (!(e.target as Element | null)?.closest?.('header li.sub')) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenu]);

  /** Menu do guia (08): abre por clique, Enter ou ↓; setas andam; Esc fecha e devolve o foco. */
  function triggerProps(id: string) {
    return {
      'aria-haspopup': 'menu' as const,
      'aria-expanded': openMenu === id,
      onClick: (e: MouseEvent<HTMLButtonElement>) => {
        const opening = openMenu !== id;
        setOpenMenu(opening ? id : null);
        // Enter ou Espaço (clique sem mouse): o foco vai para o primeiro item.
        if (opening && e.detail === 0) {
          const li = e.currentTarget.closest('li');
          requestAnimationFrame(() => menuItems(li)[0]?.focus());
        }
      },
      onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setOpenMenu(id);
          const li = e.currentTarget.closest('li');
          const last = e.key === 'ArrowUp';
          requestAnimationFrame(() => {
            const items = menuItems(li);
            items[last ? items.length - 1 : 0]?.focus();
          });
        } else if (e.key === 'Escape' && openMenu === id) {
          setOpenMenu(null);
        }
      },
    };
  }

  function menuProps() {
    return {
      onKeyDown: (e: KeyboardEvent<HTMLOListElement>) => {
        const li = e.currentTarget.closest('li');
        const items = menuItems(li);
        const i = items.indexOf(document.activeElement as HTMLButtonElement);
        const move = (n: number): void => {
          e.preventDefault();
          items[(n + items.length) % items.length]?.focus();
        };
        if (e.key === 'ArrowDown') move(i + 1);
        else if (e.key === 'ArrowUp') move(i - 1);
        else if (e.key === 'Home') move(0);
        else if (e.key === 'End') move(items.length - 1);
        else if (e.key === 'Escape') {
          e.preventDefault();
          setOpenMenu(null);
          li?.querySelector<HTMLButtonElement>(':scope > button')?.focus();
        }
      },
      // Escolher um item fecha o menu (também o aberto pelo mouse, que antes ficava aberto sob
      // o ponteiro); pelo teclado, o foco volta ao item da barra.
      onClick: (e: MouseEvent<HTMLOListElement>) => {
        if (!(e.target as Element).closest('button.hx-item')) return;
        setOpenMenu(null);
        setHoverMenu(null);
        const trigger = e.currentTarget
          .closest('li')
          ?.querySelector<HTMLButtonElement>(':scope > button');
        if (e.detail === 0) trigger?.focus();
        else (document.activeElement as HTMLElement | null)?.blur();
      },
    };
  }

  /** Menu à vista: aberto pelo clique ou pelo teclado, ou com o mouse em cima do item. */
  const menuAberto = (id: string): boolean => openMenu === id || hoverMenu === id;
  /** Classe do item da barra: aberto e página atual. */
  const subClass = (id: string, active = false): string =>
    `sub${active ? ' active' : ''}${menuAberto(id) ? ' is-open' : ''}`;
  /** Classe do menu: .ds-menu fica invisível até ter .is-aberto. */
  const menuClass = (id: string, mod: string): string =>
    `hx-menu hx-menu--${mod} ds-menu${menuAberto(id) ? ' is-aberto' : ''}`;
  /** Mouse em cima do item mostra o menu dele (e adianta a tela, como antes). */
  const hoverProps = (id: string, prefetch: () => void) => ({
    onMouseEnter: () => {
      prefetch();
      setHoverMenu(id);
    },
    onMouseLeave: () => setHoverMenu((atual) => (atual === id ? null : atual)),
  });
  // Tab para fora do item fecha o submenu.
  const closeOnBlur = (e: FocusEvent<HTMLLIElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpenMenu(null);
  };
  const newTab = String(t('hx_new_tab', '(abre em outra aba)'));
  const modules = session?.modules ?? {};
  const isLeadMachine = brand.source === 'lead-machine';
  // Grupos do WhatsApp: um endereço por marca, a mesma página.
  const whatsappGroupsPath = isLeadMachine ? '/ferramentas/grupos-whatsapp' : '/tools/whatsapp-groups';
  const onWhatsappGroups = path === '/ferramentas/grupos-whatsapp' || path === '/tools/whatsapp-groups';
  // Só com a extensão que tem o gateway do WhatsApp (ainda fora da loja).
  const showWhatsappGroups = useWhatsappGatewayAvailable(Boolean(modules['tools'])) || onWhatsappGroups;
  const isAmbassador = Boolean(session?.ambassador?.slug);

  const hasLists = Boolean(modules['emails'] || modules['phones'] || modules['places']);
  const listModules = LIST_ORDER.filter((m) => Boolean(modules[m]));
  const isListModuleActive = (m: string): boolean =>
    path === `/${m}` || path.startsWith(`/${m}/`) || path.startsWith(`/list/${m}/`);
  const isListActive = LIST_ORDER.some(isListModuleActive);
  const isAccountActive = path.startsWith('/account') || path === '/help' || path === '/referral';
  // Watchdog ligado para a conta: Licença mostra Computadores e o selo dos pedidos de acesso.
  const watchdogOn = useWatchdogStore((s) => s.enabled);
  const pendingRequests = useWatchdogStore((s) => s.pending.length);
  const { tx, date } = useAccountText();
  const openCTA = useCtaStore((s) => s.openCTA);
  const queryClient = useQueryClient();
  // Pagamentos só para a descrição do item Licença: lê o que Minha conta já buscou (ou o que o
  // mouse em cima do menu adiantou), sem pedir só por isso. Sem saber, conta com pagamentos.
  const overviewKey = accountOverviewKey(session?.userId ?? '');
  const overview = useQuery({
    queryKey: overviewKey,
    queryFn: async () => unwrap(await fetchAccountOverview()),
    enabled: false,
  });
  const prefetchOverview = (): void => {
    if (!session?.userId) return;
    void queryClient.prefetchQuery({
      queryKey: overviewKey,
      queryFn: async () => unwrap(await fetchAccountOverview()),
      staleTime: 60 * 1000,
    });
  };
  const hasPayments = overview.data ? overview.data.purchases.length > 0 : !isTrial(session);
  const license = licenseView(session);
  const licenseSub = watchdogOn
    ? hasPayments
      ? tx('menu_license_all')
      : tx('menu_license_no_pay')
    : hasPayments
      ? tx('menu_license_no_pc')
      : tx('menu_license_only');
  const pendingLabel =
    pendingRequests > 0
      ? tx(pendingRequests === 1 ? 'menu_pending_one' : 'menu_pending_other', {
          n: pendingRequests,
        })
      : undefined;
  const closeMenus = (): void => {
    setOpenMenu(null);
    setHoverMenu(null);
  };

  // Desde 17/10/2026 o eMiner usa a mesma logo da Máquina de Leads (pedido do Marcelo).
  const logoSrc = assetUrls.logoLeadMachine;

  return (
    <header>
      <div className="wrapper flex flex_center">
        <div className={`logo${logoReady ? '' : ' hide'}`}>
          {/* Link de verdade para o Início (antes era só uma imagem clicável, fora do teclado). */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            <img id="logo" src={logoSrc} alt={brand.appTitle} onLoad={() => setLogoReady(true)} />
          </a>
        </div>

        <nav id="sidebar" ref={navRef}>
          <ul>
            {modules['dashboard'] ? (
              <li className={path === '/' ? 'active' : ''}>
                <button
                  type="button"
                  id="home"
                  className="ds-resposta"
                  aria-current={path === '/' ? 'page' : undefined}
                  onClick={() => navigate('/')}
                >
                  {t('button_home', 'Início')}
                </button>
              </li>
            ) : null}

            {hasLists ? (
              <li
                className={subClass('lists', isListActive)}
                {...hoverProps('lists', () => prefetchPage('lists'))}
                onBlur={closeOnBlur}
              >
                <button type="button" id="lists" className="ds-resposta" {...triggerProps('lists')}>
                  {t('button_lists', 'Listas')}
                  <Seta />
                </button>
                <ol className={menuClass('lists', 'lists')} role="menu" {...menuProps()}>
                  {listModules.map((mod) => (
                    <MenuItem
                      key={mod}
                      id={mod}
                      current={isListModuleActive(mod)}
                      title={plainText(String(t(`module_${mod}`, mod)))}
                      onClick={() => navigate(`/${mod}`)}
                    />
                  ))}
                </ol>
              </li>
            ) : null}

            {modules['tools'] ? (
              <li
                className={subClass('tools', path === '/leadexplorer' || onWhatsappGroups)}
                {...hoverProps('tools', () => prefetchPage('leadexplorer'))}
                onBlur={closeOnBlur}
              >
                <button
                  type="button"
                  id="menu_tools"
                  className="ds-resposta"
                  {...triggerProps('tools')}
                >
                  {t('button_tools', 'Ferramentas')}
                  <Seta />
                </button>
                <ol className={menuClass('tools', 'tools')} role="menu" {...menuProps()}>
                  {modules['leadexplorer'] ? (
                    <MenuItem
                      id="leadexplorer"
                      title={t('leadexplorer', 'LeadExplorer')}
                      sub={t('hx_leadexplorer_sub', 'Investigue uma empresa pelo CNPJ ou site')}
                      current={path === '/leadexplorer'}
                      onClick={() => navigate('/leadexplorer')}
                    />
                  ) : null}
                  {showWhatsappGroups ? (
                    <MenuItem
                      id="whatsapp-groups"
                      title={t('wa_title', 'Grupos do WhatsApp')}
                      sub={t('wa_menu_sub', 'Membros e empresas dos seus grupos')}
                      badge={t('new', 'Novo')}
                      current={onWhatsappGroups}
                      onClick={() => navigate(whatsappGroupsPath)}
                    />
                  ) : null}
                  {modules['prospecta'] ? (
                    <MenuItem
                      id="prospecta"
                      title={t('hx_prospecta', 'Prospecta')}
                      sub={t('hx_prospecta_sub', 'Prospecção pelo WhatsApp')}
                      external={newTab}
                      onClick={() => {
                        window.open(
                          isLeadMachine ? externalUrls.prospectaApp : externalUrls.prospectaCom,
                        );
                      }}
                    />
                  ) : null}
                </ol>
              </li>
            ) : null}

            {isLeadMachine && modules['contents'] ? (
              <li
                className={subClass('contents', CONTENT_PATHS.includes(path))}
                {...hoverProps('contents', () => prefetchPage('multimedia'))}
                onBlur={closeOnBlur}
              >
                <button
                  type="button"
                  id="menu_contents"
                  className="ds-resposta"
                  {...triggerProps('contents')}
                >
                  {t('button_contents', 'Conteúdos')}
                  <Seta />
                </button>
                <ol className={menuClass('contents', 'contents')} role="menu" {...menuProps()}>
                  {modules['articles'] ? (
                    <MenuItem
                      id="articles"
                      title={t('articles', 'Artigos')}
                      external={newTab}
                      onClick={() => {
                        window.open(externalUrls.blog);
                      }}
                    />
                  ) : null}
                  {/* Tutorial em vídeo retirado do painel a pedido do Marcelo (29/10/2026). */}
                  {/* eBooks oculto do menu a pedido do Marcelo (14/10/2026); a rota /ebook continua. */}
                  {/* LeadTalk e LeadMusic sem o selo "Novo", a pedido do Marcelo (23/10/2026). */}
                  {has(session, 'content.items', 'leadtalk') ? (
                    <MenuItem
                      id="content_audio"
                      current={path === '/leadtalk'}
                      title={t('button_leadtalk', 'LeadTalk')}
                      sub={t('hx_leadtalk_sub', 'Podcast')}
                      onClick={() => {
                        navigate('/leadtalk');
                      }}
                    />
                  ) : null}
                  {has(session, 'content.items', 'leadmusic') ? (
                    <MenuItem
                      id="content_music"
                      current={path === '/leadmusic'}
                      title={t('button_leadmusic', 'LeadMusic')}
                      sub={t('hx_leadmusic_sub', 'Playlists para trabalhar')}
                      onClick={() => {
                        navigate('/leadmusic');
                      }}
                    />
                  ) : null}
                </ol>
              </li>
            ) : null}

            {/* Conta (decisão 195; proposta B, 25/10/2026): no lugar do Suporte, ao lado de
                Conteúdos. Primeiro o que é da conta; depois do separador, a ajuda. */}
            <li
              className={subClass('account', isAccountActive)}
              {...hoverProps('account', () => {
                prefetchPage('account');
                prefetchOverview();
                if (isAmbassador) prefetchPage('referral');
                if (!isLeadMachine && modules['help']) prefetchPage('help');
              })}
              onBlur={closeOnBlur}
            >
              <button
                type="button"
                id="menu_account"
                className="ds-resposta"
                aria-label={
                  pendingRequests > 0
                    ? tx(pendingRequests === 1 ? 'bar_pending_one' : 'bar_pending_other', {
                        n: pendingRequests,
                      })
                    : undefined
                }
                {...triggerProps('account')}
              >
                {t('button_account', 'Conta')}
                {pendingRequests > 0 ? (
                  <span className="hx-selo" aria-hidden="true">
                    {pendingRequests}
                  </span>
                ) : null}
                <Seta />
              </button>
              <ol className={menuClass('account', 'account')} role="menu" {...menuProps()}>
                {/* Topo (não é item): quem está na conta e a situação da licença, com ícone e
                    frase (a cor nunca vai sozinha) e a ação quando há o que fazer. */}
                <li className="hx-conta" role="none">
                  <span className="hx-conta__email">{session?.email ?? ''}</span>
                  {license.kind !== 'none' ? (
                    <span
                      className={`hx-conta__licenca hx-conta__licenca--${statusTone(license.kind)}`}
                    >
                      <span className="hx-conta__icone material-symbols-rounded" aria-hidden="true">
                        {statusIcon(license.kind)}
                      </span>
                      {license.kind === 'active'
                        ? tx('menu_st_active', { date: date(license.until ?? new Date()) })
                        : license.kind === 'ending'
                          ? tx(license.days === 1 ? 'menu_st_ending_one' : 'menu_st_ending_other', {
                              n: license.days,
                            })
                          : license.kind === 'today'
                            ? tx('menu_st_today')
                            : license.kind === 'expired'
                              ? tx('menu_st_expired', { date: date(license.until ?? new Date()) })
                              : license.kind === 'lifetime'
                                ? tx('menu_st_lifetime')
                                : license.kind === 'subscription'
                                  ? tx('menu_st_subscription')
                                  : tx('menu_st_trial')}
                    </span>
                  ) : null}
                  {renewIsUrgent(license.kind) ? (
                    <button
                      type="button"
                      role="menuitem"
                      tabIndex={-1}
                      className="hx-item hx-conta__acao"
                      onClick={() => {
                        closeMenus();
                        openRenewal(brand.source, session?.email ?? '', 'menu');
                      }}
                    >
                      {tx('menu_renew')}
                      <span className="material-symbols-rounded" aria-hidden="true">
                        open_in_new
                      </span>
                      <span className="hx-sr"> {tx('new_tab')}</span>
                    </button>
                  ) : license.kind === 'trial' ? (
                    <button
                      type="button"
                      role="menuitem"
                      tabIndex={-1}
                      className="hx-item hx-conta__acao"
                      onClick={() => {
                        closeMenus();
                        openCTA('default');
                      }}
                    >
                      {tx('menu_plans')}
                    </button>
                  ) : null}
                </li>
                <li className="hx-sep" role="separator" />
                <MenuItem
                  id="account_license"
                  icon="card_membership"
                  current={path === '/account'}
                  title={tx('menu_license')}
                  sub={licenseSub}
                  pill={pendingLabel}
                  onClick={() => {
                    closeMenus();
                    navigate(pendingRequests > 0 ? '/account#computadores' : '/account');
                  }}
                />
                <MenuItem
                  id="account_profile"
                  icon="person"
                  current={path === '/account/preferences'}
                  title={tx('menu_profile')}
                  sub={tx('menu_profile_sub')}
                  onClick={() => {
                    closeMenus();
                    navigate('/account/preferences');
                  }}
                />
                {/* O painel do embaixador saiu da Home (Versão 3B) e veio para cá. */}
                {isAmbassador ? (
                  <MenuItem
                    id="referral"
                    icon="redeem"
                    current={path === '/referral'}
                    title={t('menu_referral', 'Programa de recomendação')}
                    onClick={() => navigate('/referral')}
                  />
                ) : null}
                <li className="hx-sep" role="separator" />
                {/* Guia passo a passo (28/10/2026): primeiro do grupo de ajuda, em destaque. */}
                <MenuItem
                  id="guia"
                  icon="explore"
                  destaque
                  title={guiaLigado ? gtx('menu_continue') : gtx('menu_title')}
                  sub={
                    guiaLigado
                      ? gtx('menu_stopped', {
                          name: gtx(GUIA_MARCOS[Math.min(marcoDe(guiaPasso), 3)]!),
                        })
                      : gtx('menu_sub')
                  }
                  onClick={() => {
                    closeMenus();
                    const guia = useGuiaStore.getState();
                    if (guia.status !== 'ativo') comecarGuia('menu');
                    else if (guia.passo === 'escolher') guia.abrirEscolha();
                    else guia.recolher(false);
                  }}
                />
                {/* Boas-vindas de quem vem da Classic (28/10/2026): rever do primeiro cartão.
                    Só para conta da Classic (`classic` no /me). */}
                {session?.classic ? (
                  <MenuItem
                    id="novidades"
                    icon="auto_awesome"
                    title={btx('menu_title')}
                    sub={btx('menu_sub')}
                    onClick={() => {
                      closeMenus();
                      useBoasVindasStore.getState().abrirPeloMenu();
                    }}
                  />
                ) : null}
                {modules['help'] ? (
                  <MenuItem
                    id="help_center"
                    icon="help"
                    current={path === '/help'}
                    title={t('help_center', 'Central de ajuda')}
                    // Máquina de Leads: a Central de Ajuda é externa (Notion); no eMiner, página interna.
                    external={isLeadMachine ? newTab : undefined}
                    onClick={() => {
                      if (isLeadMachine) {
                        emitActivity('help.opened', { target: 'notion' });
                        window.open(externalUrls.helpLeadMachine);
                      } else {
                        navigate('/help');
                      }
                    }}
                  />
                ) : null}
                <MenuItem
                  id="contact"
                  icon="chat"
                  title={t('contact', 'Fale conosco')}
                  onClick={() => setContactOpen(true)}
                />
              </ol>
            </li>
          </ul>
          {/* Traço da página atual, um só para a barra toda (posicionarTraco). */}
          <span className="hx-traco ds-traco" ref={tracoRef} aria-hidden="true" />
        </nav>

        <div className="support" id="support_menu">
          <ul>
            <li className="capture">
              {liveRun && !captureWindow ? (
                <Suspense fallback={null}>
                  <CaptureRunButton log={liveRun} />
                </Suspense>
              ) : (
                <button
                  type="button"
                  id="capture"
                  // Resposta só na cor (.ds-resposta); a seta gira com aria-expanded (.ds-seta).
                  className={`ds-resposta${categoriesOpen ? ' is-open' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={categoriesOpen}
                  onClick={() => (categoriesOpen ? closeCategories() : openCategories())}
                >
                  {t('button_capture', 'Capturar Leads')}
                  <span
                    className="capture-caret ds-seta material-symbols-rounded"
                    aria-hidden="true"
                  >
                    expand_more
                  </span>
                </button>
              )}
            </li>
          </ul>
        </div>
      </div>

      {categoriesOpen ? (
        <CaptureCategories
          onClose={() => closeCategories()}
          onPick={(mod) => {
            closeCategories();
            // Abre a janela como OVERLAY sobre a tela atual (legado: showSearch) —
            // sem navegar: o que está no fundo (Início, listas…) permanece.
            openCaptureWindow(mod);
          }}
        />
      ) : null}

      {contactOpen ? <ContactDialog onClose={() => setContactOpen(false)} /> : null}
      {runToast ? (
        <Suspense fallback={null}>
          <CaptureRunToast />
        </Suspense>
      ) : null}
    </header>
  );
}
